import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs, Button } from '@/components/ui/daisy';
import useNewUiScope from '@/data/hook/shared/useNewUiScope';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { twMerge } from 'tailwind-merge';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import { BreadcrumbsItemProps } from '../ui/daisy/breadcrumbs/types';

type BaseBreadcrumb = Pick<
  BreadcrumbsItemProps,
  'href' | 'children' | 'icon' | 'id'
>;

type ModernLayoutProps<T extends BaseBreadcrumb> = PropsWithChildren<{
  onBack?: () => void;
  backLabel?: string;
  classNames?: Pick<ClassNameComponent, 'div' | 'base'>;
}> &
  BreadcrumbBuilderProps<T>;

type BreadcrumbBuilderProps<T extends BaseBreadcrumb> = {
  items: T[];
  onItemClick?: (item: T) => void;
};

const BreadcrumbBuilder = <T extends BaseBreadcrumb>({
  items,
  onItemClick,
}: BreadcrumbBuilderProps<T>) => (
  <Breadcrumbs>
    {items.map(item => (
      <Breadcrumbs.Item
        key={item.id}
        icon={item.icon}
        href={onItemClick ? undefined : item.href}
        onClick={onItemClick ? () => onItemClick(item) : undefined}
      >
        {item.children}
      </Breadcrumbs.Item>
    ))}
  </Breadcrumbs>
);

export default function ModernLayout<T extends BaseBreadcrumb>({
  children,
  items,
  onBack,
  backLabel,
  classNames,
  onItemClick,
}: ModernLayoutProps<T>) {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const isAppShellEnabled = useNewUiScope('appShell');

  return (
    <div
      className={twMerge(
        'flex flex-col gap-10 min-h-full bg-base-200',
        classNames?.base,
      )}
    >
      <div className="flex gap-6 items-center">
        <ConditionalRenderer condition={onBack}>
          <Button
            variant="soft"
            size="sm"
            onClick={onBack}
            className={twMerge(
              'lowercase',
              isAppShellEnabled && 'hidden lg:inline-flex',
            )}
          >
            <ChevronLeftIcon className="size-3" />
            {backLabel ?? t('back')}
          </Button>
        </ConditionalRenderer>
        <BreadcrumbBuilder items={items} onItemClick={onItemClick} />
      </div>
      <div
        className={twMerge(
          'flex gap-6 min-w-0 overflow-hidden max-w-[1980px]',
          classNames?.div,
        )}
      >
        {children}
      </div>
    </div>
  );
}
